// documents.view — preview + cetak dokumen (iframe, cetak hanya dokumen).
import { esc, pecahUnit } from '@domain/format';
import { currentUser } from '@core/session';
import { openModal } from '@ui/modal';
import { toast } from '@ui/toast';
import { kebijakan } from '@core/settings';
import { pageDims, PX_PER_MM } from '@domain/print';
import { getNota, getTandaTerima, getDocMeta, getDiwakilkan, saveDiwakilkan } from './documents.repo';
import { compressImage, uploadFile, fetchAsDataUri, FOTO } from '@core/storage';
import { billedNet } from '@domain/money';
import { kirimDokumenWA } from './documents.share';
import { notaHTML, tandaTerimaHTML, trackingHTML, labelHTML, suratKuasaHTML, docTata, type Pengambil } from './documents.doc';

/** Modal preview dokumen (iframe) + tombol cetak. Dipakai lintas-modul (mis. Statement Hutang Supplier). */
/** `kirim` = aksi "Kirim WA" opsional (dokumen untuk pelanggan). Tanda Terima tak memakainya
 *  — per Apo itu dokumen cetak yang ditandatangani di toko. */
export function previewDoc(judul: string, ikon: string, html: string, kirim?: () => Promise<void>, opts?: { tanpaCetak?: boolean; kertas?: 'A5' | 'A4'; marginMm?: number; lanjut?: { label: string; aksi: () => void } }): void {
  const bg = openModal(`
    <div class="modal-head"><h3>${ikon} ${esc(judul)}</h3><button class="x">×</button></div>
    <div class="modal-body"><div id="doc_wrap" style="overflow-x:hidden;overflow-y:auto;scrollbar-gutter:stable;border:1px solid var(--line);border-radius:8px;background:#fff"><iframe id="doc_frame" title="${esc(judul)}" style="border:0;background:#fff;display:block;transform-origin:top left"></iframe></div></div>
    <div class="modal-foot" style="flex-wrap:wrap;gap:8px"><span id="doc_pages" style="font-size:12px;color:var(--muted);flex:1;min-width:180px">Mengukur…</span><button class="btn" id="doc_close">Tutup</button>${kirim ? '<button class="btn" id="doc_wa">💬 Kirim WA ke pelanggan</button>' : ''}${opts?.tanpaCetak ? '' : '<button class="btn primary" id="doc_print">🖨️ Cetak</button>'}${opts?.lanjut ? `<button class="btn primary" id="doc_lanjut">${esc(opts.lanjut.label)}</button>` : ''}</div>`,
    // Ada lanjutan → beri tombol ← juga, supaya menutup lewat × tak membuat petugas terdampar
    // di layar kosong padahal pembayaran belum diselesaikan.
    opts?.lanjut ? { onBack: opts.lanjut.aksi } : undefined);
  const frame = bg.querySelector('#doc_frame') as HTMLIFrameElement;
  // Ukuran cetak dari Pengaturan → 🖨️ Cetak. Bawaan ½ A4 = A4 dipotong MELINTANG
  // (210 lebar × 148 tinggi, melebar) — bukan A5 tegak.
  const kertas = pageDims(opts?.kertas ?? (kebijakan().printSize === 'A4' ? 'A4' : 'A5'), opts?.marginMm);
  // CATATAN JUJUR: penomoran halaman otomatis (`@page { @bottom-center { counter(page) } }`)
  // TIDAK didukung Chrome/Safari/Firefox — aturannya dibuang diam-diam. Yang benar-benar
  // bekerja: elemen `position:fixed` terulang di SETIAP halaman cetak. Jadi tiap lembar
  // dicap nomor dokumennya → lembar kedua yang tertinggal di printer masih bisa dikenali
  // milik nota mana. Nomor "hal 1/2" sungguhan butuh pustaka paginasi (Paged.js) — belum dipasang.
  //
  // `.doc` bawaan dibatasi 560px (selebar kertas TEGAK). Di kertas melebar batas itu menyisakan
  // ruang kosong hampir sepertiga halaman, jadi lebarnya diikutkan ukuran kertas — di layar
  // maupun di cetakan, supaya yang dilihat Apo = yang keluar dari printer.
  const gaya = opts?.tanpaCetak ? '<style>body{margin:0;padding:0}</style>' : `<style>
    @page{size:${kertas.css};margin:${kertas.marginCss}}
    html,body{width:${kertas.isiMm}mm}
    /* Dokumen memberi padding pada body untuk tampilan layar dan menolkannya saat cetak.
       Pratinjau ini MEWAKILI KERTAS (margin sudah diurus @page), jadi padding itu dinolkan
       juga di sini — kalau tidak, yang dilihat Apo lebih tinggi daripada hasil cetak dan
       peringatan "berapa lembar" ikut salah (nota terukur 131 mm padahal tercetak ±123 mm). */
    /* margin:0 juga — dokumen Label tak menolkannya sendiri, sehingga terdorong 8px (2mm) ke
       kanan dan berisiko terpotong pada printer yang tak presisi. */
    body{padding:0;margin:0}
    /* .doc (tanda terima/kuasa) 560px, body (nota) & .card (tracking) 420px = batas warisan
       kertas TEGAK. Di kertas melebar batas itu menyisakan sepertiga halaman kosong sementara
       tinggi kertas justru menyusut, jadi dokumen malah jadi 2 lembar. Ikutkan lebar kertas. */
    body,.doc,.card{max-width:${kertas.isiMm}mm;width:100%}
    /* Cap nomor dokumen diletakkan relatif ke AREA ISI (bukan margin), jadi tanpa ruang bawah
       ia menimpa baris terakhir tiap lembar. */
    /* Bantalan kiri-kanan dipasang di SINI, bukan di @page: pilihan "Margins: None" di dialog
       cetak menimpa @page, tapi tidak menimpa padding. Bawah 8mm menampung cap nomor dokumen. */
    @media print{html,body{width:auto}body{padding:0 ${kertas.sampingMm}mm 8mm}}
    .pg-cap{display:none}
    @media print{.pg-cap{display:block;position:fixed;bottom:2mm;left:0;right:0;text-align:center;
      font:8.5px Arial;color:#777;letter-spacing:.3px}}
  </style>`;
  // Gaya ditaruh SESUDAH dokumen supaya menang atas CSS bawaan dokumen (kekhususan sama).
  frame.srcdoc = html + gaya
    + (opts?.tanpaCetak ? ''
      : `<div class="pg-cap">${esc(judul)} · dicetak ${new Date().toLocaleDateString('id-ID')}</div>`);
  // Peringatan luber: diukur dari iframe SETELAH render — Apo tahu sebelum menekan Cetak.
  // Lebar kertas sesungguhnya (px). Dokumen dirender seukuran ini lalu DIPERKECIL agar muat
  // di layar — kalau dirender langsung selebar layar HP, isinya membungkus lebih banyak,
  // tingginya jadi lain, dan hitungan "berapa lembar" ikut salah.
  // Dokumen kirim-WA dibaca di HP, bukan di kertas → pratinjaunya pakai lebar telepon supaya
  // yang Apo atur = yang diterima pelanggan.
  const lebarPx = opts?.tanpaCetak ? 420 : Math.round(kertas.isiMm * PX_PER_MM);
  frame.style.width = `${lebarPx}px`;
  frame.addEventListener('load', () => {
    const d = frame.contentDocument; if (!d) return;
    const tinggiHal = kertas.isiTinggiMm * PX_PER_MM;             // AREA ISI, bukan tinggi kertas penuh
    const tinggiIsi = d.body.scrollHeight || 0;
    const lembar = Math.ceil(tinggiIsi / tinggiHal);
    const wrap = bg.querySelector('#doc_wrap') as HTMLElement | null;
    if (wrap) {
      const tinggiPakai = Math.max(tinggiIsi, tinggiHal);
      // Skala dihitung ULANG tiap kali kotak pratinjau berubah ukuran — kalau hanya dihitung
      // sekali, menekan ⤢ (perbesar) membuat jendelanya melebar tapi dokumennya tetap kecil.
      let lebarLalu = -1, besarLalu: boolean | null = null;
      const pasKan = (): void => {
        // Berhenti kalau masukannya tak berubah. Fungsi ini MENGUBAH tinggi wrap sedangkan
        // wrap itu sendiri yang diamati; di layar pendek bilah gulir modal bisa muncul-hilang
        // bergantian dan membuat pratinjau berkedip tanpa henti.
        const besar = !!bg.querySelector('.modal')?.classList.contains('maximized');
        if (wrap.clientWidth === lebarLalu && besar === besarLalu) return;
        lebarLalu = wrap.clientWidth; besarLalu = besar;
        // `scrollbar-gutter:stable` mencadangkan ruang bilah gulir, jadi lebarnya tak
        // berubah-ubah saat gulir muncul/hilang. `-2` = ruang garis tepi.
        const skala = Math.min(1, Math.max(0.2, (wrap.clientWidth - 2) / lebarPx));
        frame.style.height = `${tinggiPakai}px`;            // tanpa gulir dalam gulir
        frame.style.transform = `scale(${skala})`;
        // `scale` tak mengubah tinggi tata letak → tanpa ini ada ruang kosong panjang saat digulir.
        frame.style.marginBottom = `${-(tinggiPakai * (1 - skala))}px`;
        const maks = besar ? window.innerHeight * 0.72 : window.innerHeight * 0.58;
        wrap.style.height = `${Math.min(maks, tinggiPakai * skala)}px`;
      };
      pasKan();
      const ro = new ResizeObserver(pasKan); ro.observe(wrap);
      // Lepaskan saat jendela ditutup lewat jalur mana pun (Tutup / X / Kirim WA / Lanjut) —
      // kalau tidak, observer + isi dokumennya (Surat Kuasa membawa foto & tanda tangan base64)
      // tetap tergenggam sepanjang tab hidup.
      new MutationObserver((_m, self) => { if (!bg.isConnected) { ro.disconnect(); self.disconnect(); } })
        .observe(document.body, { childList: true });
    }
    const info = bg.querySelector('#doc_pages') as HTMLElement | null;
    if (info && opts?.tanpaCetak) info.innerHTML = '<span style="color:var(--muted)">Dikirim ke pelanggan lewat WhatsApp — tidak dicetak.</span>';
    else if (info) info.innerHTML = lembar > 1
      ? `<b style="color:var(--warn)">⚠️ Akan tercetak ${lembar} lembar</b> (${esc(kertas.label)}) — tiap lembar dicap nomor dokumen. Bila ingin satu lembar, setel <b>ukuran kertas dokumen ini</b> ke A4 di Pengaturan → 📄 Dokumen & Template (jangan ubah setelan umum — nota & tanda terima justru pas di ½ A4).`
      : `<span style="color:var(--ok)">✔ Muat 1 lembar ${esc(kertas.label)}</span>`;
  });
  (bg.querySelector('#doc_close') as HTMLElement).addEventListener('click', () => bg.remove());
  bg.querySelector('#doc_print')?.addEventListener('click', () => {
    frame.contentWindow?.focus(); frame.contentWindow?.print();
  });
  bg.querySelector('#doc_lanjut')?.addEventListener('click', () => { bg.remove(); opts?.lanjut?.aksi(); });
  bg.querySelector('#doc_wa')?.addEventListener('click', async (ev) => {
    const b = ev.currentTarget as HTMLButtonElement;
    b.disabled = true; b.textContent = '⏳ Menyiapkan tautan…';      // unggah butuh sedetik — cegah klik ganda
    try { await kirim?.(); bg.remove(); }
    catch (e) { toast((e as Error).message); b.disabled = false; b.textContent = '💬 Kirim WA ke pelanggan'; }
  });
}

export async function previewNota(no: string): Promise<void> {
  try {
    const d = await getNota(no);
    const html = notaHTML(d);
    const u = pecahUnit(d.unit);
    previewDoc(`Nota — ${no}`, '🧾', html, () => kirimDokumenWA({
      no, outlet: d.ticket.outlet, jenis: 'nota servis', judul: `Nota Servis ${no}`, html,
      waPelanggan: d.custWa, nama: d.custNama, merk: u.merk, tipe: u.tipe, total: billedNet(d.ticket),
    }), { kertas: docTata('nota').kertas, marginMm: docTata('nota').marginMm });
  } catch (e) { toast((e as Error).message); }
}

export async function previewTandaTerima(no: string, opts?: { garansi?: boolean }): Promise<void> {
  try { previewDoc(`Tanda Terima${opts?.garansi ? ' GARANSI' : ''} — ${no}`, '📄', tandaTerimaHTML(await getTandaTerima(no), opts), undefined, { kertas: docTata('tanda').kertas, marginMm: docTata('tanda').marginMm }); }
  catch (e) { toast((e as Error).message); }
}

/** Kartu Tracking = dikirim ke pelanggan lewat WA, TIDAK dicetak (keputusan Apo 21 Jul). */
export async function previewTracking(no: string): Promise<void> {
  try {
    const m = await getDocMeta(no);
    const html = trackingHTML(m);
    previewDoc(`Tracking — ${no}`, '🔗', html, () => kirimDokumenWA({
      no, outlet: m.outlet, jenis: 'kartu status servis', judul: `Status Servis ${no}`, html,
      waPelanggan: m.custWa, nama: m.custNama, merk: m.merk, tipe: m.model, total: 0,
    }), { tanpaCetak: true });
  } catch (e) { toast((e as Error).message); }
}

export async function previewLabel(no: string): Promise<void> {
  try { previewDoc(`Label — ${no}`, '🏷️', labelHTML(await getDocMeta(no)), undefined, { kertas: docTata('label').kertas, marginMm: docTata('label').marginMm }); }
  catch (e) { toast((e as Error).message); }
}

/** Surat Kuasa: isi data pengambil + FOTO WAJIB (pengambil pegang unit, per v18) → simpan + cetak.
 *  Foto → Supabase Storage bucket 'kuasa' (folder = outlet, RLS cabang). Data persist `tickets.diwakilkan`. */
export async function previewSuratKuasa(no: string, onBack?: () => void, lanjut?: { label: string; aksi: () => void }): Promise<void> {
  let meta: Awaited<ReturnType<typeof getDocMeta>>;
  try { meta = await getDocMeta(no); } catch (e) { toast((e as Error).message); return; }
  const d = await getDiwakilkan(no); // graceful null pra-0054
  const pp = d?.pengambil;
  const bg = openModal(`<div class="modal-head"><h3>📝 Surat Kuasa (Diwakilkan) — ${esc(no)}</h3><button class="x">×</button></div>
    <div class="modal-body">
      <div class="scope-note">Pemilik: <b>${esc(meta.custNama)}</b> · Unit: ${esc(meta.unit)}. Isi identitas <b>pengambil</b> (yang mewakili) + <b>foto bukti</b>.</div>
      ${d ? '<div class="scope-note" style="color:var(--ok)">✔ Sudah pernah diisi — data & foto dimuat. Ganti bila perlu.</div>' : ''}
      <div class="field"><label>Nama Pengambil (sesuai KTP) <span class="req">*</span></label><input id="dk_nama" value="${esc(pp?.nama ?? '')}"></div>
      <div class="row2"><div class="field"><label>No. KTP</label><input id="dk_ktp" value="${esc(pp?.ktp ?? '')}"></div>
        <div class="field"><label>No. HP</label><input id="dk_hp" value="${esc(pp?.hp ?? '')}" placeholder="62812…"></div></div>
      <div class="field"><label>Alamat (KTP)</label><input id="dk_alamat" value="${esc(pp?.alamat ?? '')}"></div>
      <div class="field"><label>Hubungan dengan Pemilik</label><input id="dk_hub" value="${esc(pp?.hubungan ?? '')}" placeholder="mis. Suami / Karyawan / Saudara"></div>
      <div class="field"><label>📷 Foto bukti — pengambil memegang unit <span class="req">*</span></label>
        <input type="file" id="dk_foto" accept="image/*" capture="environment">
        <div id="dk_fotowrap" style="margin-top:6px">${d?.foto ? '<div class="scope-note" style="font-size:12px">Foto tersimpan — biarkan kosong untuk memakai yang lama, atau pilih foto baru untuk mengganti.</div>' : ''}</div></div>
      <div class="field"><label>✍️ Tanda tangan pengambil ${d?.ttd ? '<span style="color:var(--ok);font-weight:400">— sudah ada, coret lagi untuk mengganti</span>' : ''}</label>
        <canvas id="dk_ttd" width="600" height="180" style="width:100%;max-width:320px;height:110px;border:1px dashed var(--line);border-radius:8px;background:#fff;touch-action:none"></canvas>
        <div style="margin-top:4px"><button type="button" class="btn" id="dk_ttdclear" style="padding:3px 10px;font-size:12px">Ulangi</button>
          <span style="font-size:11.5px;color:var(--muted);margin-left:6px">Coret dengan jari atau tetikus. Ini pengesahan bila surat tak dicetak.</span></div></div>
      <div class="err" id="dk_err"></div>
    </div>
    <div class="modal-foot"><button class="btn" id="dk_cancel">Batal</button><button class="btn primary" id="dk_go">📝 Buat Surat Kuasa</button></div>`, { onBack });

  // Tanda tangan layar — cukup canvas + pointer events, tanpa pustaka tambahan.
  const kanvas = bg.querySelector('#dk_ttd') as HTMLCanvasElement;
  const ctx = kanvas.getContext('2d');
  let adaCoretan = false;
  if (ctx) {
    ctx.lineWidth = 3; ctx.lineCap = 'round'; ctx.strokeStyle = '#111';
    let menggambar = false;
    const titik = (e: PointerEvent): [number, number] => {
      const r = kanvas.getBoundingClientRect();
      return [(e.clientX - r.left) * (kanvas.width / r.width), (e.clientY - r.top) * (kanvas.height / r.height)];
    };
    kanvas.addEventListener('pointerdown', (e) => {
      menggambar = true; kanvas.setPointerCapture(e.pointerId);
      const [x, y] = titik(e); ctx.beginPath(); ctx.moveTo(x, y);
    });
    kanvas.addEventListener('pointermove', (e) => {
      if (!menggambar) return;
      // Ditandai di SINI, bukan saat menekan: sekali ketuk tanpa menggeser tidak menggambar
      // apa pun, dan kalau dianggap "ada coretan" ia menimpa tanda tangan lama dengan kosong.
      adaCoretan = true;
      const [x, y] = titik(e); ctx.lineTo(x, y); ctx.stroke();
    });
    const selesai = (): void => { menggambar = false; };
    kanvas.addEventListener('pointerup', selesai);
    kanvas.addEventListener('pointercancel', selesai);   // telepon berdering / gestur sistem
  }
  bg.querySelector('#dk_ttdclear')?.addEventListener('click', () => {
    ctx?.clearRect(0, 0, kanvas.width, kanvas.height); adaCoretan = false;
  });
  const val = (id: string): string => (bg.querySelector('#' + id) as HTMLInputElement | null)?.value.trim() ?? '';
  const fotoInput = bg.querySelector('#dk_foto') as HTMLInputElement;
  const fotoWrap = bg.querySelector('#dk_fotowrap') as HTMLElement;
  fotoInput.addEventListener('change', () => {
    const f = fotoInput.files?.[0];
    if (f) { const url = URL.createObjectURL(f); fotoWrap.innerHTML = `<img src="${url}" style="max-width:160px;max-height:160px;border:1px solid var(--line);border-radius:6px">`; }
  });
  bg.querySelector('#dk_cancel')?.addEventListener('click', () => bg.remove());
  const goBtn = bg.querySelector('#dk_go') as HTMLButtonElement;
  goBtn.addEventListener('click', async () => {
    const err = bg.querySelector('#dk_err') as HTMLElement;
    const p: Pengambil = { nama: val('dk_nama'), ktp: val('dk_ktp'), alamat: val('dk_alamat'), hp: val('dk_hp'), hubungan: val('dk_hub') };
    if (!p.nama) { err.textContent = 'Nama pengambil wajib diisi.'; return; }
    const file = fotoInput.files?.[0] ?? null;
    if (!file && !d?.foto) { err.textContent = 'Foto bukti wajib (pengambil memegang unit).'; return; } // WAJIB per v18
    goBtn.disabled = true; err.textContent = '';
    try {
      let fotoPath = d?.foto ?? null;
      if (file) { const blob = await compressImage(file, FOTO.identitas.dim, FOTO.identitas.q); fotoPath = await uploadFile('kuasa', meta.outlet || 'ALL', blob, 'jpg', 'image/jpeg'); }
      // Coretan baru menggantikan yang lama; tanpa coretan, tanda tangan lama dipertahankan.
      const ttdUrl = adaCoretan ? kanvas.toDataURL('image/png') : (d?.ttd ?? null);
      await saveDiwakilkan(no, { pengambil: p, foto: fotoPath, ttd: ttdUrl, at: new Date().toISOString(), by: currentUser()?.nama ?? '' });
      // Foto lama TAK dihapus otomatis (bisa bukti sesi lama). Sisa foto ganti = orphan → dibersihkan manual di Pengaturan → Penyimpanan.
      const fotoUrl = fotoPath ? await fetchAsDataUri('kuasa', fotoPath) : null; // base64 → dokumen self-contained (arsip/save-PDF)
      bg.remove();
      const cs = currentUser()?.nama ?? '—';
      previewDoc(`Surat Kuasa — ${no}`, '📝', suratKuasaHTML(meta, p, cs, fotoUrl, false, { ttdUrl }), () => kirimDokumenWA({
        no, outlet: meta.outlet, jenis: 'surat kuasa pengambilan', judul: `Surat Kuasa ${no}`,
        // SALINAN PELANGGAN TANPA FOTO: foto bukti memuat wajah + KTP pengambil. Tautan WA bisa
        // diteruskan ke siapa saja, jadi foto identitas TIDAK ikut dikirim — ia tetap ada di
        // lembar cetak & arsip toko sebagai bukti.
        // Tanda tangan TIDAK ikut ke salinan pelanggan: tautan WA bisa diteruskan siapa saja
        // dan gambar tanda tangan cukup jelas untuk dijiplak. Alasannya sama dengan foto & NIK.
        // Yang otentik ada di lembar/arsip toko.
        html: suratKuasaHTML(meta, p, cs, null, true),
        waPelanggan: meta.custWa, nama: meta.custNama, merk: meta.merk, tipe: meta.model, total: 0,
      }), { kertas: docTata('kuasa').kertas, marginMm: docTata('kuasa').marginMm, ...(lanjut ? { lanjut } : {}) });
    } catch (e) { err.textContent = (e as Error).message; goBtn.disabled = false; }
  });
}
